import dayjs, { Dayjs, QUnitType, UnitType } from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import isBetween from "dayjs/plugin/isBetween";
import utc from "dayjs/plugin/utc";
import minMax from "dayjs/plugin/minMax";
import customParseFormat from "dayjs/plugin/customParseFormat";
import {
  DATE_TIME_UNITS,
  dayofWeekFormate,
  defaultDateFormat,
  talentDateFormat,
  TIME_ZONE
} from "src/config/constants/genericConstants";
import timezone from "dayjs/plugin/timezone";
import { ToastAlert } from "src/components/HeaderLink/interface";

dayjs.extend(customParseFormat);
dayjs.extend(quarterOfYear);
dayjs.extend(utc);
dayjs.extend(isoWeek);
dayjs.extend(isBetween);
dayjs.extend(minMax);
dayjs.extend(timezone);

export type dateParamType = string | number | Date | dayjs.Dayjs;

export const DEFAULT_DATE_FORMAT = "MM/DD/YYYY";
export const DEFAULT_TIME_FORMAT = "hh:mm A";

export const getYesterday = (format = DEFAULT_DATE_FORMAT) => [
  dayjs().subtract(1, "day").format(format),
  dayjs().subtract(1, "day").format(format)
];

export const getToday = (format = DEFAULT_DATE_FORMAT) => [
  dayjs().format(format),
  dayjs().format(format)
];

export const getLastWeek = (format = DEFAULT_DATE_FORMAT) => {
  const lastWeekStart = dayjs()
    .subtract(1, "week")
    .startOf("week")
    .format(format);
  const lastWeekEnd = dayjs().subtract(1, "week").endOf("week").format(format);
  return [lastWeekStart, lastWeekEnd];
};

export const getLastWeekForDate = (
  date: dateParamType,
  format = DEFAULT_DATE_FORMAT
) => {
  const lastWeekStart = dayjs(date)
    .subtract(1, "week")
    .startOf("week")
    .format(format);
  const lastWeekEnd = dayjs(date)
    .subtract(1, "week")
    .endOf("week")
    .format(format);
  return [lastWeekStart, lastWeekEnd];
};

export const getCurrentWeek = (format = DEFAULT_DATE_FORMAT) => {
  const currentWeekStart = dayjs().startOf("week").format(format);
  const currentWeekEnd = dayjs().endOf("week").format(format);
  return [currentWeekStart, currentWeekEnd];
};

export const getLastFourWeeks = (format = DEFAULT_DATE_FORMAT) => {
  const lastFourWeekStart = dayjs()
    .subtract(4, "weeks")
    .startOf("week")
    .format(format);
  const lastFourWeekEnd = dayjs()
    .subtract(1, "week")
    .endOf("week")
    .format(format);
  return [lastFourWeekStart, lastFourWeekEnd];
};

export const getLastWeekFromDay = (
  format = DEFAULT_DATE_FORMAT,
  weekCount = 1
): Array<string> => {
  const weekStart = dayjs().subtract(weekCount, "week").format(format);
  const weekEnd = dayjs().format(format);
  return [weekStart, weekEnd];
};

export const getMonthToDate = (format = DEFAULT_DATE_FORMAT) => {
  const monthStart = dayjs().startOf("month").format(format);
  const toDate = dayjs().format(format);
  return [monthStart, toDate];
};

export const getQuarterToDate = (format = DEFAULT_DATE_FORMAT) => {
  const quarterStart = dayjs().startOf("quarter").format(format);
  const toDate = dayjs().format(format);
  return [quarterStart, toDate];
};

export const getLastSixMonthsToDate = (format = DEFAULT_DATE_FORMAT) => {
  const start = dayjs().subtract(6, "months").format(format);
  const end = dayjs().format(format);
  return [start, end];
};

export const getOneYearDate = (format = DEFAULT_DATE_FORMAT) => {
  const start = dayjs().subtract(12, "months").format(format);
  const end = dayjs().format(format);
  return [start, end];
};

export const getYearToDate = (format = DEFAULT_DATE_FORMAT) => {
  const yearStart = dayjs().startOf("year").format(format);
  const toDate = dayjs().format(format);
  return [yearStart, toDate];
};

export const getWeekToDate = (format = DEFAULT_DATE_FORMAT) => {
  const weekStart = dayjs().startOf("week").format(format);
  const toDate = dayjs().format(format);
  return [weekStart, toDate];
};
export const getLastQuater = (format = DEFAULT_DATE_FORMAT) => {
  const today = new Date();
  const quarter = Math.floor(today.getMonth() / 3);
  const startFullQuarter = new Date(today.getFullYear(), quarter * 3 - 3, 1);
  const endFullQuarter = new Date(
    startFullQuarter.getFullYear(),
    startFullQuarter.getMonth() + 3,
    0
  );
  return [
    dayjs(startFullQuarter).format(format),
    dayjs(endFullQuarter).format(format)
  ];
};

export const getLastMonth = (format = DEFAULT_DATE_FORMAT) => {
  const lastMonthStart = dayjs()
    .subtract(1, "month")
    .startOf("month")
    .format(format);
  const lastMonthEnd = dayjs()
    .subtract(1, "month")
    .endOf("month")
    .format(format);
  return [lastMonthStart, lastMonthEnd];
};

export const getSevenDaysFromYesterday = (format = DEFAULT_DATE_FORMAT) => {
  const start = dayjs().subtract(7, "days").format(format);
  const end = dayjs().subtract(1, "day").format(format);
  return [start, end];
};

export const getLastThirtyDays = (format = DEFAULT_DATE_FORMAT) => {
  const start = dayjs().subtract(30, "days").format(format);
  const end = dayjs().format(format);
  return [start, end];
};

export const getLastNinetyDays = (format = DEFAULT_DATE_FORMAT) => {
  const start = dayjs().subtract(90, "days").format(format);
  const end = dayjs().format(format);
  return [start, end];
};

export const getLastOneMonth = (format = DEFAULT_DATE_FORMAT) => {
  const start = dayjs().subtract(1, "month").format(format);
  const end = dayjs().format(format);
  return [start, end];
};

export const getLastTwoWeeks = (format = DEFAULT_DATE_FORMAT) => {
  const start = dayjs().subtract(2, "weeks").format(format);
  const end = dayjs().format(format);
  return [start, end];
};

export const getLastSixMonthsWeekBasedFromAndToDate = (
  format = DEFAULT_DATE_FORMAT
) => {
  const start = dayjs().subtract(6, "months").startOf("week").format(format);
  const end = dayjs().format(format);
  return [start, end];
};

export const getStartAndEndDatesOfPeriod = (period: string) => {
  switch (period) {
    case "L4W": {
      return getLastFourWeeks();
    }
    case "LQ": {
      return getLastQuater();
    }
    case "LW": {
      return getLastWeek();
    }
    case "MTD": {
      return getMonthToDate();
    }
    case "QTD": {
      return getQuarterToDate();
    }
    case "YTD": {
      return getYearToDate();
    }
    case "WTD": {
      return getWeekToDate();
    }
    case "Y": {
      return getYesterday();
    }
    case "T": {
      return getToday();
    }
    case "LM": {
      return getLastMonth();
    }
    case "L7D": {
      return getLastSevenDays();
    }
    case "L30D": {
      return getLastThirtyDays();
    }
    case "L90D": {
      return getLastNinetyDays();
    }
    default: {
      return getLastFourWeeks();
    }
  }
};

export const getDateBasedOnOptionSelection = (id: string) => {
  switch (id) {
    case "allDays": {
      return getSevenDaysFromYesterday();
    }
    case "yesterday": {
      return getYesterday();
    }
  }
};

export const getFormattedDate = (
  value: dateParamType,
  format = DEFAULT_DATE_FORMAT,
  isUtc = false
) => {
  if (!value) return "";
  return isUtc ? dayjs.utc(value).format(format) : dayjs(value).format(format);
};

export const getStartDate = (dateParams: any) => {
  return dayjs(dateParams.fromDate).format("MM/DD/YY");
};
export const getEndDate = (dateParams: any) => {
  return dayjs(dateParams.toDate).format("MM/DD/YY");
};

export const getDateTimeStamp = (date: dateParamType) => {
  return dayjs(date).unix();
};

export const getDateTimeStampMilliseconds = (date: dateParamType): number => {
  return dayjs(date).valueOf();
};

export const getFormatedDate = (date: any, format = defaultDateFormat) => {
  if (!date) return "";
  return dayjs(date).format(format);
};

export const getFormattedDateFromDifferentFormate = (
  date: any,
  inputFormate: string,
  outputFormate = DEFAULT_DATE_FORMAT
) => {
  if (!date) return "";
  return dayjs(date, inputFormate).format(outputFormate);
};

export const getISOString = (value: any) => {
  return dayjs(value).toISOString();
};

export const getstartOfDateISOString = (value: any) => {
  return dayjs(value).endOf("date").toISOString();
};

export const getJSDate = (
  value: string,
  format: string = DEFAULT_DATE_FORMAT
) => dayjs(value, format).toDate();

export const getLastSevenDays = (format = DEFAULT_DATE_FORMAT) => {
  const fromDate = dayjs().subtract(6, "day").format(format);
  const toDate = dayjs().format(format);
  return [fromDate, toDate];
};

export const getThirteenWeekBackDate = (
  selectedDate: any,
  format = DEFAULT_DATE_FORMAT
) => {
  const lastThirteenWeekStart = dayjs(selectedDate)
    .subtract(12, "weeks")
    .format(format);
  return lastThirteenWeekStart;
};

export const getLastDayOccurance = (day: number, format: string) => {
  const dayINeed = day;
  const today = dayjs().isoWeekday();

  // if we haven't yet passed the day of the week that I need:
  if (today <= dayINeed) {
    // otherwise, give me *prev week's* instance of that same day
    return dayjs().subtract(1, "weeks").isoWeekday(dayINeed).format(format);
  } else {
    // then just give me this week's instance of that day
    return dayjs().isoWeekday(dayINeed).format(format);
  }
};

export const getExperiy = (expiry: string) => {
  return expiry ? dayjs(expiry).format("MM/DD/YY") : "";
};

export const fromatTime = (time: string, formate = DEFAULT_TIME_FORMAT) => {
  return dayjs(time, "HH:mm:ss").format(formate);
};

export const getLastFortyEightHrsStartAndEnd = (
  format = DEFAULT_DATE_FORMAT
) => {
  return [dayjs().subtract(48, "hours").format(format), dayjs().format(format)];
};

export const timeDifference = (
  time1: string,
  time2: string,
  unit: dayjs.ManipulateType
): number => {
  return dayjs(time1).diff(dayjs(time2), unit);
};

export const subtractTimeToDate = (
  date: dateParamType,
  value: number,
  unit: dayjs.ManipulateType
) => {
  return dayjs(date).subtract(value, unit);
};

export const getDateIndex = (date: string | number) => {
  return dayjs(date).day();
};

export const getWeekStartAndEnd = (
  date: dateParamType,
  format = DEFAULT_DATE_FORMAT
) => {
  const weekStart = dayjs(date).startOf("week").format(format);
  const weekEnd = dayjs(date).endOf("week").format(format);
  return [weekStart, weekEnd];
};

export const getPhyInvStartDate = (
  weekStart: dateParamType,
  weekEnding: dateParamType,
  phyInvDate: string[]
): string => {
  const dates = phyInvDate
    ?.filter(
      (date: string) =>
        dayjs(date).isBetween(weekStart, weekEnding) ||
        dayjs(date).isSame(weekStart)
    )
    .sort((a, b) => {
      return dayjs(a).unix() - dayjs(b).unix();
    })
    .reverse();
  const date = dates?.[0];
  return date;
};

export const addToDate = (
  date: dateParamType,
  value: number,
  unit: dayjs.QUnitType
): number => {
  return getDateTimeStampMilliseconds(dayjs(date).add(value, unit));
};

export const getStartof = (unit: dayjs.OpUnitType, date?: dateParamType) => {
  return dayjs(date).utc().startOf(unit);
};

/**
 * Gets the UTC time of the start of a date (midnight UTC)
 * @param date - The date to get the start of
 * @returns Dayjs object representing midnight UTC of the given date
 */
export const getStartOfDateUTC = (date: dateParamType): Dayjs => {
  return dayjs.utc(date).startOf("day");
};

export const getDaysAfterDate = (
  date: dateParamType,
  count: number,
  format = "YYYY-MM-DD"
) => dayjs(date, format).add(count, "days");

export const getDaysBeforeDate = (
  date: dateParamType,
  count: number,
  format = "YYYY-MM-DD"
) => dayjs(date, format).subtract(count, "days");

export const isAfterDate = (
  date: dateParamType,
  refDate: dateParamType = ""
) => {
  const dateObj = dayjs(date);
  const refDateObj = refDate ? dayjs(refDate) : dayjs();
  return dateObj.isAfter(refDateObj, "days");
};

export const isBeforeDate = (date: dateParamType, refDate = "") => {
  const dateObj = dayjs(date);
  const refDateObj = refDate ? dayjs(refDate) : dayjs();
  return dateObj.isBefore(refDateObj, "days");
};

export const getMinimumDate = (dateObjects: dayjs.Dayjs[]) => {
  return dayjs.min(dateObjects);
};

export const getMaximumDate = (dateObjects: dayjs.Dayjs[]) => {
  return dayjs.max(dateObjects);
};

export const getLastSevenDaysOptions = (format = DEFAULT_DATE_FORMAT) => {
  let start = dayjs().subtract(7, "day");
  const end = dayjs().subtract(2, "day");

  const datesArray = [];
  while (start <= end) {
    const newDate = dayjs(start).add(1, "day");
    const formatedNewDate = {
      id: dayjs(start).format(format),
      name: `${dayjs(start).format("dddd")} - ${dayjs(start).format(
        "MM/DD/YY"
      )}`
    };
    datesArray.push(formatedNewDate);
    // start date will be incremented
    start = newDate;
  }
  return datesArray.reverse();
};

export const getDatesBetween = (
  fromDate: string,
  toDate: string,
  format = DEFAULT_DATE_FORMAT
) => {
  let start = dayjs(fromDate);
  const end = dayjs(toDate);
  const datesArray = [];
  while (start <= end) {
    const newDate = dayjs(start).add(1, "day");
    const formatedDate = dayjs(start).format(format);
    datesArray.push(formatedDate);
    // start date will be incremented
    start = newDate;
  }
  return datesArray;
};

export const getMaxDateForGMR = () => {
  // Convert the current date and time to the PST timezone
  const currentDateTimePST = dayjs().tz("America/Los_Angeles");

  // Target date and time 12:00 PM PST
  const targetDateTimePST = dayjs()
    .tz("America/Los_Angeles")
    .set("hour", 12)
    .set("minute", 0);

  if (currentDateTimePST.isAfter(targetDateTimePST)) {
    return targetDateTimePST.subtract(1, "day").toDate();
  } else {
    return targetDateTimePST.subtract(2, "days").toDate();
  }
};

/**
 * By default it will return 1 year ago date from today
 */
export const getDateBySubtractedMonth = (
  date = new Date(new Date().toISOString()),
  differeceMonth = 12
) => {
  const d = new Date(date.setMonth(date.getMonth() - differeceMonth));
  d.setDate(d.getDate() + 1);
  return d;
};

export const getTimeZone = (): string => {
  return dayjs.tz.guess();
};

export const isTodayAfterReferenceDate = (refDate: any) =>
  dayjs().isAfter(refDate, "days");

export const getSameDayOfLastYear = (date: string): string => {
  const inputDate = dayjs(date);
  const dayOfWeek = inputDate.day();

  // Subtract one year and get the same day of the week
  let lastYearSameDay = inputDate.subtract(1, "year");

  // Adjust to the same day of the week if necessary
  while (lastYearSameDay.day() !== dayOfWeek) {
    lastYearSameDay = lastYearSameDay.add(1, "day");
  }

  return lastYearSameDay.format("MM/DD/YYYY");
};

export const getLastThreeMonths = (format = DEFAULT_DATE_FORMAT) => {
  const start = dayjs().subtract(3, "month").format(format);
  const end = dayjs().format(format);
  return [start, end];
};

export const getTimeRemaining = (date: Date | dayjs.Dayjs) => {
  const days = timeDifference(date.toString(), new Date().toString(), "d");
  date = subtractTimeToDate(date.toString(), days, "d");
  const hours = timeDifference(date.toString(), new Date().toString(), "h");
  date = subtractTimeToDate(date.toString(), hours, "h");
  const minutes = timeDifference(date.toString(), new Date().toString(), "m");
  return `${days}d ${hours}h ${minutes}m Left`;
};

export const getTimeLeftForAlertMsgAction = (
  item: ToastAlert
): [number, Date] => {
  const timeStamp = getDateTimeStamp(item?.CreatedDate) * 1000;
  const expirydatetime: Date = new Date(timeStamp + Number(item?.Sla) * 60000);
  const timeremaining = expirydatetime.getTime() - new Date().getTime();
  return [timeremaining, expirydatetime];
};
export const getLastSaturday = (date: dateParamType = new Date()): Dayjs => {
  if (!date) return dayjs();
  const Today = dayjs(date);
  const startOfWeek = Today.startOf("w");
  const lastSaturday = startOfWeek.day(-1);
  return lastSaturday;
};

export const isSameDay = (
  date1: dateParamType,
  date2: dateParamType,
  unit: QUnitType = "day"
) => {
  return dayjs(date1).isSame(date2, unit);
};

export const getDayjsObj = (date: dateParamType): Dayjs => {
  return dayjs(date);
};

/** Get all the date before the end date */
export const getDatesBeforeEndDate = (
  endDate: dateParamType,
  dateList: string[],
  unit: QUnitType = "day"
): string[] => {
  const dates = dateList
    ?.filter((date: string) => dayjs(date).isBefore(endDate, unit))
    .sort((a, b) => {
      return dayjs(a).unix() - dayjs(b).unix();
    })
    .reverse();
  return dates;
};

export const formatDateOfWeek = (
  date: dateParamType,
  format = dayofWeekFormate
): string => {
  return dayjs(date).format(format);
};

export const isDateOutsidePST = (dateString: string): boolean => {
  const inputDate = dayjs.tz(dateString, "MM/DD/YYYY", "America/Los_Angeles");
  const nowInPST = dayjs().tz("America/Los_Angeles");
  const saturdayStart = nowInPST
    .startOf("week")
    .subtract(1, "day")
    .startOf("day"); // Previous Saturday 12:00 AM
  const mondayEnd = nowInPST.startOf("week").add(1, "day").endOf("day"); // Current Monday 11:59 PM
  const isInAllowedWindow = nowInPST.isBetween(
    saturdayStart,
    mondayEnd,
    null,
    "[]"
  );
  if (inputDate.isSame(nowInPST, "day")) {
    return false;
  }

  if (isInAllowedWindow) {
    // Define the previous week range (Sunday to Saturday)
    const lastSunday = nowInPST
      .startOf("week")
      .subtract(1, "week")
      .startOf("day"); // Last Sunday
    const lastSaturday = lastSunday.add(6, "day").endOf("day"); // Last Saturday
    // Check if the input date is within the previous week
    const isInPreviousWeek = inputDate.isBetween(
      lastSunday,
      lastSaturday,
      null,
      "[]"
    );
    return !isInPreviousWeek;
  }
  return true;
};

export const getYearBefore = (date: string, year: number = 1) => {
  return dayjs(date).subtract(year, "year").format("MM/DD/YYYY");
};

export const dynamicAddDate = (
  date: dateParamType,
  count: number,
  format = "YYYY-MM-DD",
  type = "days"
) =>
  dayjs(date)
    .add(count, type as QUnitType)
    .format(format);

/**
 * Filters dates within a specified date range
 * @param dates Array of objects containing date strings
 * @param startDate Start of date range
 * @param endDate End of date range
 * @param dateKey Key in the object to access the date string
 * @param dateFormat Format of the date string (default: YYYY-MM-DD)
 * @param boundary Boundary string for inclusivity/exclusivity (default: "[]")
 * @returns Array of dates that fall within the range
 */
export const getDatesInRange = (
  dates: Array<{ [key: string]: string }>,
  startDate: dayjs.ConfigType,
  endDate: dayjs.ConfigType,
  dateKey: string,
  dateFormat: string = "YYYY-MM-DD",
  boundary: "[]" | "()" | "(]" | "[)" = "[]"
): string[] => {
  const start = dayjs(startDate).startOf("day");
  const end = dayjs(endDate).endOf("day");

  return dates
    .filter((dateObj) => {
      const date = dayjs(dateObj[dateKey], dateFormat);
      if (date.isValid()) {
        return date.isBetween(start, end, "day", boundary);
      }
      return false;
    })
    .map((dateObj) => dateObj[dateKey]);
};

/**
 * Gets Saturday of the week for a given date (considering week starts on Sunday)
 * @param date Input date in any valid format
 * @param format Output format (default: MM/DD/YYYY)
 * @returns Saturday date in specified format
 */
export const getSaturdayOfWeek = (
  date: dateParamType,
  format: string = DEFAULT_DATE_FORMAT
): string => {
  const inputDate = dayjs(date);
  // Calculate days to add based on Sunday-as-week-start logic:
  // 0 (Sunday) -> 6 days to add, 1 (Monday) -> 5 days, ..., 6 (Saturday) -> 0 days
  const daysToAdd = 6 - inputDate.day();
  return inputDate.add(daysToAdd, "day").format(format);
};

const subtractDuration = (
  date: dayjs.Dayjs,
  count: number,
  type = "day" as QUnitType
) => date.subtract(count, type);

const getDifferenceDate = (
  date: dayjs.Dayjs,
  monthDifference: number,
  dateDifference: number,
  format: string
) => {
  let currentDate = date;
  if (monthDifference > 0) {
    currentDate = subtractDuration(currentDate, monthDifference, "month");
  }
  if (dateDifference > 0) {
    currentDate = subtractDuration(currentDate, dateDifference);
  }
  return currentDate.format(format);
};

export const getCustomDifferenceDate = (
  options: {
    date?: dateParamType;
    format?: string;
    startDateDifferenceDay?: number;
    endDateDifferenceDay?: number;
    startDateDifferenceMonth?: number;
    endDateDifferenceMonth?: number;
  } = {}
) => {
  const {
    date = new Date(),
    format = DEFAULT_DATE_FORMAT,
    startDateDifferenceDay = 0,
    endDateDifferenceDay = 0,
    startDateDifferenceMonth = 0,
    endDateDifferenceMonth = 0
  } = options;
  const today = dayjs(date);
  const startDate = getDifferenceDate(
    today,
    startDateDifferenceMonth,
    startDateDifferenceDay,
    format
  );
  const endDate = getDifferenceDate(
    today,
    endDateDifferenceMonth,
    endDateDifferenceDay,
    format
  );
  return [startDate, endDate];
};

export const isDateDifferenceWithinLimit = (
  fromDate: string,
  toDate: string,
  dayCount: number
) => {
  const daysDifference = timeDifference(toDate, fromDate, "days");
  return daysDifference <= dayCount;
};

/**
 * Returns the date of the most recent Saturday in PST timezone.
 *
 * Logic for determining the last Saturday:
 * - If current time is Sunday before noon PST: Returns Saturday from previous week
 * - If current time is any other day: Returns the most recent Saturday
 * - If current time is Saturday after noon PST: Returns the current Saturday
 *
 * @param {dateParamType} [date=new Date()] - Reference date (defaults to current date)
 * @returns {string} Date string in 'YYYY-MM-DD' format
 */
export const getGMRLastSaturdayDate = (
  date: dateParamType = new Date()
): string => {
  // Convert input date to PST timezone
  const currentDateTimePST = dayjs(date).tz(TIME_ZONE.AMERICA_LOS_ANGELES);

  // Define noon PST for boundary condition
  const noonPST = dayjs()
    .tz(TIME_ZONE.AMERICA_LOS_ANGELES)
    .set(DATE_TIME_UNITS.HOUR as UnitType, 12)
    .set(DATE_TIME_UNITS.MINUTE as UnitType, 0)
    .set("millisecond", 0);

  // Get current day (0 = Sunday, 6 = Saturday)
  const currentDay = currentDateTimePST.day();
  let lastSaturday: Dayjs;

  // Special handling for Sunday before noon PST
  if (currentDateTimePST.isBefore(noonPST) && currentDay === 0) {
    lastSaturday = currentDateTimePST.subtract(
      8,
      DATE_TIME_UNITS.DAYS as UnitType
    );
  } else {
    // Calculate days to subtract to reach last Saturday
    const daysToSubtract = (currentDay % 7) + 1;
    lastSaturday = currentDateTimePST.subtract(
      daysToSubtract,
      DATE_TIME_UNITS.DAY as UnitType
    );
  }

  return lastSaturday.format(defaultDateFormat);
};

export const getDateInPST = (toDate: string | number | Date) => {
  return dayjs.tz(toDate, "America/Los_Angeles");
};

/**
 * Gets the UTC offset for a given timezone in minutes.
 *
 * This function calculates the difference between the specified timezone
 * and UTC in minutes. Positive values indicate timezones ahead of UTC,
 * negative values indicate timezones behind UTC.
 *
 * @param {string} [timezone=TIME_ZONE.UTC] - The timezone to get the offset for
 * @param {dateParamType} [date=new Date()] - Reference date (defaults to current date)
 * @returns {number} The UTC offset in minutes
 */
export const getUTCOffset: (date?: dateParamType) => number = (
  date: dateParamType = new Date()
): number => {
  const dateInTimezone = dayjs(date).tz(getTimeZone());
  return dateInTimezone.utcOffset();
};

export const calculateTimeRemaining = (time: Dayjs) => {
  const currentTime: Dayjs = dayjs();
  return time.diff(currentTime);
};

export const getExpiryTimeForFormatAlert = (date: string, duration: number) =>
  dayjs(date).add(duration, "m");

export const checkWeekEndInCurrentWeek = (weekEnd: dateParamType) => {
  const currentWeekEnd = getSaturdayOfWeek(weekEnd);
  return dayjs(currentWeekEnd).isSame(dayjs(), "week");
};

export const getNextWeekEnd = (
  weekEnd: dateParamType = new Date(),
  dateFormat = talentDateFormat
) => {
  const currentWeekEnd = getSaturdayOfWeek(weekEnd);
  return dayjs(currentWeekEnd).add(1, "week").format(dateFormat);
};

export const getCurrentDay = () => {
  return dayjs().day();
};

export const isSameOrAfterDate = (
  date: dateParamType,
  refDate: dateParamType
) => {
  return isAfterDate(date, refDate) || isSameDay(date, refDate);
};

export const isSameOrBeforeDate = (
  date: dateParamType,
  refDate: dateParamType
) => {
  return isBeforeDate(date, refDate.toString()) || isSameDay(date, refDate);
};

// return the next day after noon of the given date in the given format
export const getNextDayNoonUTC = (date: dateParamType) => {
  return dayjs(date)
    .startOf("day")
    .add(1, "day")
    .hour(12)
    .minute(0)
    .millisecond(0);
};

// check date1 is before date2
export const isBeforeDateTime = (
  date1: dateParamType = "",
  date2: dateParamType = "",
  unit: UnitType = "days"
) => {
  const date1Obj = dayjs(date1);
  const date2Obj = dayjs(date2);

  return date1Obj.isBefore(date2Obj, unit);
};

export const isAfter955AMPST = (): boolean => {
  // Get current time in PST
  const currentTimePST = dayjs().tz(TIME_ZONE.AMERICA_LOS_ANGELES);

  // Create 9:55 AM PST for today
  const nineFiftyFiveAMPST = dayjs()
    .tz(TIME_ZONE.AMERICA_LOS_ANGELES)
    .set("hour", 9)
    .set("minute", 55)
    .set("second", 0)
    .set("millisecond", 0);

  // Check if current time is after 9:55 AM PST
  return currentTimePST.isAfter(nineFiftyFiveAMPST);
};
